import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { isOlderBrowser } from '../utils/browserDetection';
import { AuthCompat } from './AuthCompat';
import { OnboardingCarousel } from '../components/onboarding/OnboardingCarousel';
import { getCarouselSlides } from '../components/onboarding/onboardingSlides';
import { AuthSheets } from '../components/onboarding/AuthSheets';
import { AuthPantallaCompleta } from '../components/onboarding/AuthPantallaCompleta';
import { type AuthMode } from '../components/onboarding/AuthForm';
import { useIsDesktop } from '../components/onboarding/useIsDesktop';
import { captureAttributionParams, saveSignupAttribution } from '../lib/signupAttribution';
import { getPostLoginDest } from '../lib/postLoginDest';
import { ONBOARDING_V2 } from '../components/onboarding/onboardingV2Routes';

/**
 * `/auth` — la ÚNICA entrada a Atlas (ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real).
 *
 * Reemplaza al form clásico y monta el diseño nuevo, **conservando el contrato de URL
 * intacto** porque hay links externos (landing, anuncios, mails) que no podemos tocar:
 *
 * - `/auth` → iniciar sesión (default de siempre).
 * - `/auth?mode=signup` → el **carrusel** de onboarding, cuyo CTA lleva al registro.
 * - `/auth?mode=reset` → restablecer contraseña.
 * - `/auth?next=/algo` → al entrar, va ahí (lo usa el plugin de Premiere).
 * - `/auth?rubro=moda` → el carrusel con los fondos y el copy de ese rubro (campañas,
 *   antes `/bienvenida/:rubro`; ver ADR-785).
 * - Con sesión ya abierta no renderiza nada: es el **retorno del OAuth de Google**.
 *
 * En **desktop** el layout es split: el form fijo a la izquierda y el carrusel a la
 * derecha, siempre — no hay "carrusel o form", se ven los dos. En **mobile** el modo
 * decide: signup muestra el carrusel (el registro vive en `/registro`, a pantalla
 * completa) y signin/reset muestran el form directo.
 *
 * Navegadores viejos siguen yendo a `AuthCompat`, igual que antes.
 */

const TITLE_ID = 'auth-title';

/** `?mode=` de la URL → modo del formulario. Sin param (o basura): iniciar sesión. */
function modeDeLaUrl(raw: string | null): AuthMode {
  if (raw === 'signup') return 'signup';
  if (raw === 'reset') return 'forgot';
  return 'login';
}

export const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isDesktop = useIsDesktop();

  // Compat de navegadores viejos: se resuelve en el INICIALIZADOR del estado, no en
  // un efecto. El `Auth.tsx` viejo lo hacía en un `useEffect` con un `return` antes
  // del resto de los hooks: al pasar a compat cambiaba la cantidad de hooks entre
  // renders (error de React). Acá el valor ya está en el primer render.
  const [isCompat] = useState(() => {
    try {
      return isOlderBrowser();
    } catch {
      // Si la detección falla, compat: es el modo que anda en todos lados.
      return true;
    }
  });

  const modeUrl = modeDeLaUrl(searchParams.get('mode'));
  const rubro = searchParams.get('rubro') ?? undefined;

  // Destino post-login (`?next=`), capturado UNA vez: recalcularlo después leería la
  // URL ya sin el param.
  const [postLoginDest] = useState(getPostLoginDest);

  // En desktop el panel de auth está SIEMPRE abierto (es la mitad izquierda del
  // split). En mobile sólo cuando el modo pide formulario; con `signup` se ve el
  // carrusel y el form vive en `/registro`.
  const [authMode, setAuthMode] = useState<AuthMode | null>(
    isDesktop || modeUrl !== 'signup' ? modeUrl : null,
  );
  useEffect(() => {
    setAuthMode(isDesktop || modeUrl !== 'signup' ? modeUrl : null);
  }, [isDesktop, modeUrl]);

  // Atribución (UTM / ref / cookies de Meta) apenas aterriza, ANTES de que toque
  // nada: si se capturara al hacer submit, un usuario que llega con `?utm_source=…`
  // y navega al carrusel o a `/registro` perdería la campaña.
  useEffect(() => {
    captureAttributionParams();
  }, []);

  // Sesión ya presente al montar = venimos del retorno del OAuth de Google (o el
  // usuario abrió /auth estando logueado) → adentro, sin mostrar el form.
  useEffect(() => {
    if (isCompat) return;
    let activo = true;
    supabase.auth.getSession().then(({ data }) => {
      if (activo && data.session) navigate(postLoginDest, { replace: true });
    });
    return () => {
      activo = false;
    };
  }, [navigate, postLoginDest, isCompat]);

  // Alta por Google: el `signUp` no pasa por acá (lo resuelve el OAuth), así que la
  // atribución se escribe al volver. Se considera alta NUEVA si el usuario se creó
  // hace menos de 60s — mismo criterio que el `Auth.tsx` clásico.
  useEffect(() => {
    if (isCompat) return;
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event !== 'SIGNED_IN' || !session) return;
      const esOAuth = session.user.app_metadata?.provider !== 'email';
      const creado = session.user.created_at ? new Date(session.user.created_at).getTime() : 0;
      const esAltaFresca = creado > 0 && Date.now() - creado < 60_000;
      if (esOAuth && esAltaFresca) {
        await saveSignupAttribution(session.user.id);
      }
      navigate(postLoginDest, { replace: true });
    });
    return () => subscription.unsubscribe();
  }, [navigate, postLoginDest, isCompat]);

  if (isCompat) return <AuthCompat />;

  // Cambio de modo en la pantalla completa de mobile: además de mover el estado, limpia
  // el `?mode=` de la URL conservando el resto de los params (`next`, `rubro`, UTMs).
  //
  // Sin la limpieza, un refresh devolvería al usuario al modo viejo y no al que está
  // viendo — el caso concreto es entrar por `/auth?mode=reset`, volver al login y
  // recargar: aparecía otra vez "Restablecer contraseña". En la práctica sólo hay algo
  // que limpiar viniendo de `?mode=reset`: `?mode=signup` se va por la rama del carrusel
  // y cualquier otro valor ya cae en login.
  const cambiarModo = (m: AuthMode) => {
    setAuthMode(m);
    if (searchParams.get('mode') === null) return;
    const params = new URLSearchParams(searchParams);
    params.delete('mode');
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
  };

  // Mobile + login/reset: pantalla completa con el form, sin carrusel ni bottom sheet
  // (el sheet flotante quedó sólo para el panel de desktop).
  if (!isDesktop && modeUrl !== 'signup') {
    const modoActual = authMode ?? modeUrl;
    return (
      <AuthPantallaCompleta
        mode={modoActual}
        // El mismo camino que el back: así el link al pie del form y el botón del header
        // dejan la URL igual, en vez de uno limpiarla y el otro no.
        onModeChange={cambiarModo}
        titleId={TITLE_ID}
        // Back sólo en "restablecer contraseña": es el único modo con una pantalla
        // anterior propia (el login). A `/auth` en login se entra directo por URL, no
        // hay adónde volver.
        onBack={modoActual === 'forgot' ? () => cambiarModo('login') : undefined}
      />
    );
  }

  // CTA del carrusel (sólo mobile): al registro a pantalla completa, arrastrando el
  // query string para no perder `?ref=`/UTMs, y con `from` para que el back vuelva acá.
  const handleStart = () => {
    navigate(`${ONBOARDING_V2.registro}${location.search}`, {
      state: { from: `${location.pathname}${location.search}` },
    });
  };

  return (
    <>
      <OnboardingCarousel
        slides={getCarouselSlides(rubro)}
        onStart={handleStart}
        // En mobile se pausa mientras el form tapa el carrusel; en desktop corre
        // siempre (el split muestra los dos a la vez).
        paused={!isDesktop && authMode !== null}
      />
      <AuthSheets
        mode={authMode}
        onModeChange={setAuthMode}
        // Mobile: cerrar el form vuelve al carrusel. Desktop: la ventana es fija.
        onClose={() => {
          if (!isDesktop) setAuthMode(null);
        }}
      />
    </>
  );
};

export default Auth;
